import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { storeToRefs } from 'pinia';
import { until } from '@vueuse/core';
import toNonNullable from '@/utils/toNonNullable';

import 'vue-router';

declare module 'vue-router' {
  interface RouteMeta {
    layout?: 'dashboard' | 'auth';
    requiresAuth?: boolean;
  }
}

const isStatic = import.meta.env.VITE_STATIC === 'true'; // for landing routing

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: toNonNullable([
    isStatic
      ? { path: '/', component: () => import('@/landing/TheLanding.vue'), name: 'home' }
      : undefined,
    {
      path: '/login',
      component: () => import('@/components/TheLogin.vue'),
      name: 'login',
      meta: { layout: 'auth' },
    },
    {
      path: '/register',
      component: () => import('@/components/TheRegistration.vue'),
      name: 'register',
      meta: { layout: 'auth' },
    },
    {
      path: '/register/confirm',
      component: () => import('@/components/TheRegistrationConfirm.vue'),
      name: 'register-confirm',
      meta: { layout: 'auth' },
    },
    {
      path: '/password-reset',
      component: () => import('@/components/ThePasswordReset.vue'),
      name: 'password-reset',
      meta: { layout: 'auth' },
    },
    {
      path: '/password-reset/confirm',
      component: () => import('@/components/ThePasswordResetConfirm.vue'),
      name: 'password-reset-confirm',
      meta: { layout: 'auth' },
    },
    {
      path: '/share/',
      name: 'share',
      component: () => import('@/components/SharedConversationView.vue'),
      props: true,
    },
    {
      path: '/welcome',
      name: 'onboarding',
      component: () => import('@/components/TheOnboarding.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/',
      meta: { requiresAuth: true, layout: 'dashboard', name: 'dashboard' },
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/components/TheHome.vue'),
          meta: { layout: 'dashboard' },
        },
        {
          path: 'goals',
          name: 'goals',
          component: () => import('@/components/GoalManager.vue'),
          props: { timeline: 'upcoming' },
          meta: { layout: 'dashboard' },
        },
        {
          path: 'goals/new',
          name: 'goals-create',
          component: () => import('@/components/GoalCreateWizard.vue'),
          meta: { layout: 'dashboard' },
        },
        {
          path: 'goals/:id',
          name: 'goals-detail',
          component: () => import('@/components/GoalDetail.vue'),
          props: true,
          meta: { layout: 'dashboard' },
        },
        {
          path: 'goals/:id/edit',
          name: 'goals-detail-edit',
          component: () => import('@/components/MessageEditForm.vue'),
          props: true,
          meta: { layout: 'dashboard' },
        },
        {
          path: 'achievements',
          component: () => import('@/components/TheAchievements.vue'),
          meta: { layout: 'dashboard' },
          children: [
            {
              path: '',
              name: 'achievements',
              component: () => import('@/components/GoalManager.vue'),
              props: { timeline: 'past', bare: true },
            },
            {
              path: 'calendar',
              name: 'achievements-calendar',
              component: () => import('@/components/AchievementHeatmap.vue'),
            },
            {
              path: 'diary',
              name: 'achievements-diary',
              component: () => import('@/components/AchievementDiary.vue'),
            },
            {
              path: 'week/:week?',
              name: 'achievements-week',
              component: () => import('@/components/AchievementWeek.vue'),
              props: true,
            },
          ],
        },
        {
          path: 'achievements/:id(\\d+)',
          name: 'achievements-detail',
          component: () => import('@/components/GoalDetail.vue'),
          props: true,
          meta: { layout: 'dashboard' },
        },
        // Old links and bookmarks keep working.
        { path: 'milestones', redirect: { name: 'goals' } },
        { path: 'milestones/new', redirect: { name: 'goals-create' } },
        {
          path: 'milestones/:id',
          redirect: (to) => ({ name: 'goals-detail', params: { id: to.params.id } }),
        },
        {
          path: 'milestones/:id/edit',
          redirect: (to) => ({ name: 'goals-detail-edit', params: { id: to.params.id } }),
        },
        {
          path: 'identities',
          name: 'identities',
          component: () => import('@/components/TagManager.vue'),
          meta: { layout: 'dashboard' },
        },
        { path: 'tags', redirect: { name: 'identities' } },
        {
          path: 'profile',
          component: () => import('@/components/TheProfile.vue'),
          meta: { layout: 'dashboard' },
          children: [
            {
              path: '',
              name: 'profile',
              component: () => import('@/components/TheProfileInfo.vue'),
            },
            {
              path: 'password',
              name: 'password',
              component: () => import('@/components/TheProfilePassword.vue'),
            },
            {
              path: 'notifications',
              name: 'notifications',
              component: () => import('@/components/TheSettings.vue'),
            },
            {
              path: 'subscription',
              name: 'subscription',
              component: () => import('@/components/TheProfileSubscription.vue'),
            },
          ],
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('@/components/ContactBook.vue'),
          meta: { layout: 'dashboard' },
        },
        {
          path: 'time-capsules',
          name: 'time-capsules',
          component: () => import('@/components/TimeCapsuleArchive.vue'),
          meta: { layout: 'dashboard' },
        },
        {
          path: 'feedback',
          name: 'feedback',
          component: () => import('@/components/TheFeedback.vue'),
          meta: { layout: 'dashboard' },
        },
        {
          path: ':pathMatch(.*)*',
          name: 'dashboard-not-found',
          component: () => import('@/components/TheDashboardNotFound.vue'),
          meta: { layout: 'dashboard' },
        },
      ],
    },
  ]),
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const { isAuthenticated, initialized, me, onboardingPrompted } = storeToRefs(authStore);
  await until(initialized).toBe(true);
  if (to.meta.requiresAuth && !isAuthenticated.value) {
    return { path: '/login' };
  }
  if (
    to.meta.requiresAuth &&
    to.name !== 'onboarding' &&
    me.value?.onboarding_state == null &&
    !onboardingPrompted.value
  ) {
    onboardingPrompted.value = true;
    return { name: 'onboarding' };
  }
});

function back() {
  const appHostnames = ['app.tahani.io', 'localhost'];

  function isAppHostname(hostname: string): boolean {
    return appHostnames.some((domain) => hostname === domain || hostname.startsWith(`${domain}:`));
  }

  const referrer = document.referrer;
  if (referrer) {
    try {
      const referrerUrl = new URL(referrer);
      if (isAppHostname(referrerUrl.hostname)) {
        window.history.back();
        return;
      }
    } catch {
      // Invalid referrer, proceed to fallback
    }
  }

  router.push('/');
}

router.back = back;

export default router;
